import { Field, Float, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop, PropType } from '@typegoose/typegoose'
import { BaseEntity } from '../base/BaseEntity'
import { User } from '../user/User'
import { RelatedEntity } from '../base/RelatedEntity'
import { WorkOrder } from '../work-order/WorkOrder'
import { TaskConversation } from './TaskConversation'
import type { TaskStatusEnum } from './TaskStatusEnum'
import { Activity } from '../activity/Activity'
import { Project } from '../project/Project'
import { Asset } from '../asset/Asset'
import { generateEntityId } from '../generateEntityId'
import { TaskStatusActionConfig } from './task-action-hook/TaskStatusActionConfig'

@ObjectType()
@InputType('TaskInput')
@modelOptions({ schemaOptions: { timestamps: true } })
export class Task extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('task') })
  declare _id: string

  @Field(() => [User])
  @Prop({ type: [String] }) // it will be stored as string in db
  members: User[]

  @Field(() => Date)
  @Prop({ type: Date })
  startDate: Date

  @Field(() => Date)
  @Prop({ type: Date })
  endDate: Date

  @Field(() => Float)
  @Prop({ type: Number, default: 0 })
  timeSpent: number

  @Field(() => WorkOrder)
  @Prop({ type: RelatedEntity })
  workOrder: ReturnType<() => WorkOrder>

  @Field(() => [Task])
  @Prop({ type: [RelatedEntity] }, PropType.ARRAY)
  subTasks: ReturnType<() => Task[]>

  //we need to reference the parent here
  @Field(() => Task)
  @Prop({ type: RelatedEntity })
  parentTask: ReturnType<() => Task>

  @Field(() => String)
  @Prop({ type: String })
  description: string

  @Field(() => TaskConversation)
  @Prop({ type: TaskConversation })
  conversation: TaskConversation

  @Field(() => String)
  @Prop({ type: String })
  declare status: TaskStatusEnum

  @Field(() => [Activity])
  @Prop({ type: [Activity] })
  remarksActivity: Activity[]

  @Field(() => [Asset])
  @Prop({ type: [Asset] })
  assets: Asset[]

  /**
   * This is the estimated percentage of completion
   */
  @Field(() => Float)
  @Prop({ type: Number, default: 0 })
  percentageComplete: number

  @Field(() => Float, { nullable: true })
  @Prop({ type: Number, default: 0 })
  laborCost: number

  @Field(() => Float, { nullable: true })
  @Prop({ type: Number, default: 0 })
  estimatedDuration: number

  @Field(() => Float, { nullable: true })
  @Prop({ type: Number, default: 0 })
  perUnitCost: number

  @Field(() => Float, { nullable: true })
  @Prop({ type: Number, default: 0 })
  total: number

  /**
   * Configured status-to-action mappings for this task.
   * Defines which action hooks should be executed when the task transitions to specific statuses.
   */
  @Field(() => [TaskStatusActionConfig], { nullable: true })
  @Prop({ type: [TaskStatusActionConfig] }, PropType.ARRAY)
  statusActions?: TaskStatusActionConfig[]

  /**
   * The following fields has fieldResolvers, we just need it here for type-safety
   */

  @Field(() => Project)
  @Prop({ type: RelatedEntity })
  project: ReturnType<() => Project>
}
